import { motion } from 'framer-motion'
import { Building2, Eye, MessageCircle, Clock } from 'lucide-react'
import { cn } from '@/utils/cn'
import { formatCurrencyCompact } from '@/utils/format-currency'
import { calcProbability, getInitials, getProbColor } from '@/components/RocketSearch/rocketKanbanUtils'
import type { Deal, DealStage } from '@/types'

interface RocketKanbanCardProps {
  deal: Deal
  stage: DealStage
  draggedDealId: string | null
  onDragStart: (e: React.DragEvent<HTMLDivElement>, dealId: string) => void
  onDragEnd: (e: React.DragEvent<HTMLDivElement>) => void
  onDealClick?: (deal: Deal) => void
  onDelete?: (dealId: string) => void
}

export function RocketKanbanCard({
  deal,
  stage,
  draggedDealId,
  onDragStart,
  onDragEnd,
  onDealClick,
  onDelete,
}: RocketKanbanCardProps) {
  const stageProb = calcProbability(stage)

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      draggable
      // @ts-expect-error framer-motion onDragStart conflicts with native HTML drag
      onDragStart={(e: React.DragEvent<HTMLDivElement>) => onDragStart(e, deal.id)}
      // @ts-expect-error framer-motion onDragEnd conflicts with native HTML drag
      onDragEnd={(e: React.DragEvent<HTMLDivElement>) => onDragEnd(e)}
      className={cn(
        'group cursor-grab rounded-lg border border-[var(--glass-border)] bg-[var(--glass-bg)] p-3 transition-all active:cursor-grabbing hover:border-[var(--brand-primary)]/30',
        draggedDealId === deal.id && 'opacity-50',
        deal.stagnant && 'shadow-[0_0_12px_rgba(239,68,68,0.15)] ring-1 ring-red-500/30',
      )}
      onClick={() => onDealClick?.(deal)}
    >
      <div className='flex items-start justify-between gap-2'>
        <div className='flex min-w-0 items-center gap-1.5'>
          <Building2 size={12} className='shrink-0 text-[var(--brand-primary)]' />
          <span className='truncate text-xs font-medium text-[var(--font-color-body)]'>
            {deal.empresaNome}
          </span>
          {deal.proposalViewedAt && (
            <span title='Proposta visualizada'>
              <Eye size={11} className='shrink-0 text-emerald-400' />
            </span>
          )}
        </div>
        <div className='flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100'>
          {deal.telefone && (
            <a
              href={`https://wa.me/55${deal.telefone.replace(/\D/g, '')}`}
              target='_blank'
              rel='noopener noreferrer'
              onClick={(e) => e.stopPropagation()}
              className='rounded p-1 text-emerald-400 transition-colors hover:bg-white/[0.06] hover:text-emerald-300'
              title='Abrir WhatsApp'
            >
              <MessageCircle size={11} />
            </a>
          )}
          {onDelete && (
            <button
              type='button'
              onClick={(e) => {
                e.stopPropagation()
                onDelete(deal.id)
              }}
              className='rounded p-1 text-[var(--text-muted)] transition-colors hover:bg-white/[0.06] hover:text-red-400'
              title='Mover para lixeira'
            >
              <Clock size={11} />
            </button>
          )}
        </div>
      </div>

      <div className='mt-2 flex items-center justify-between'>
        <span className='text-sm font-bold text-[var(--font-color-h1)]'>
          {formatCurrencyCompact(deal.valor)}
        </span>
        <span
          className='rounded px-1.5 py-0.5 text-[10px] font-medium'
          style={{
            backgroundColor: `${getProbColor(stageProb)}20`,
            color: getProbColor(stageProb),
          }}
        >
          {stageProb}%
        </span>
      </div>

      <div className='mt-1.5 flex items-center gap-1.5 text-[10px] text-[var(--font-color-muted)]'>
        <div className='flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-purple-500/20 text-[7px] font-bold text-purple-400'>
          {getInitials(deal.closerNome)}
        </div>
        <span className='truncate'>{deal.closerNome}</span>
      </div>

      <div className='mt-2 h-1 overflow-hidden rounded-full bg-white/[0.06]'>
        <div
          className='h-full rounded-full transition-all'
          style={{
            width: `${stageProb}%`,
            backgroundColor: getProbColor(stageProb),
          }}
        />
      </div>
    </motion.div>
  )
}
